import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useStyles } from './styles';
import { Box, Typography, CircularProgress, IconButton } from '@mui/material';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import bgBtn from '../../assets/images/btn-back.png';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import cx from 'classnames';
import { useDispatch } from 'react-redux';
import { setSelectedGenre, resetPodcastsByGenre } from '../../redux/slices/podcastsSlice';
import { PodcastCard } from '../PodcastCard/PodcastCard';
import { getPodcastIdFromUrl } from '../../utils/updatePodcastUrl';
const NextArrow = (props) => {
  const { onClick, currentSlide, slideCount } = props;
  const isLastSlide = currentSlide === slideCount - 5;

  return (
    <div
      className={cx('slick-arrow', 'slick-next', {
        'slick-disabled': isLastSlide,
      })}
      onClick={onClick}
    >
      <ArrowForwardIosIcon />
    </div>
  );
};

const PrevArrow = (props) => {
  const { onClick, currentSlide } = props;

  return (
    <div
      className={cx('slick-arrow', 'slick-prev', {
        'slick-disabled': currentSlide === 0,
      })}
      onClick={onClick}
    >
      <ArrowBackIosNewIcon />
    </div>
  );
};

const getSliderSettings = (filteredPodcasts) => ({
  dots: false,
  infinite: false,
  speed: 1000,
  touchThreshold: 100,
  slidesToShow: Math.min(5, filteredPodcasts.length),
  slidesToScroll: Math.min(5, filteredPodcasts.length),
  nextArrow: <NextArrow />,
  prevArrow: <PrevArrow />,
  variableWidth: false,
  waitForAnimate: true,
  useCSS: true,
  useTransform: true,
  swipe: false,
  responsive: [
    {
      breakpoint: 1200,
      settings: {
        slidesToShow: Math.min(4, filteredPodcasts.length),
        slidesToScroll: Math.min(4, filteredPodcasts.length),
      },
    },
    {
      breakpoint: 900,
      settings: {
        slidesToShow: Math.min(3, filteredPodcasts.length),
        slidesToScroll: Math.min(3, filteredPodcasts.length),
      },
    },
    {
      breakpoint: 600,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
  ],
});

const PodcastsCarousel = () => {
  const { selectedGenre, podcastsByGenre, status, currentPodcast, activePlatform } = useSelector(
    (state) => state.podcasts
  );
  const [activeSlides, setActiveSlides] = useState([0]);
  const [isVisible, setIsVisible] = useState(false);
  const [isAutoScrolling, setIsAutoScrolling] = useState(false);
  const dispatch = useDispatch();
  const classes = useStyles({ bgBtn });
  const sliderRef = useRef(null);
  const loading = status === 'loading';
  const timersRef = useRef([]);

  const podId = getPodcastIdFromUrl();

  const clearAllTimers = () => {
    if (timersRef.current && timersRef.current.length > 0) {
      timersRef.current.forEach((timer) => clearTimeout(timer));
      timersRef.current = [];
    }
  };

  const setAndTrackTimeout = (callback, delay) => {
    const timerId = setTimeout(() => {
      timersRef.current = timersRef.current.filter((id) => id !== timerId);
      callback();
    }, delay);

    timersRef.current.push(timerId);
    return timerId;
  };

  useEffect(() => {
    setIsVisible(false);
    clearAllTimers();

    setAndTrackTimeout(() => {
      setIsVisible(true);
    }, 100);

    return () => {
      clearAllTimers();
    };
  }, [selectedGenre]);

  useEffect(() => {
    return () => {
      clearAllTimers();
    };
  }, []);

  useEffect(() => {
    if (selectedGenre && podcastsByGenre[selectedGenre]?.length > 0) {
      setIsVisible(false);
      clearAllTimers();

      setAndTrackTimeout(() => {
        setIsVisible(true);
      }, 500);
    }
  }, [activePlatform]);

  useEffect(() => {
    if (!podcastsByGenre[selectedGenre]?.length) {
      clearAllTimers();
      setIsVisible(false);
      dispatch(setSelectedGenre(null));
    }
  }, [podcastsByGenre, selectedGenre, dispatch, activePlatform]);

  const filteredPodcasts = useMemo(() => {
    return selectedGenre ? podcastsByGenre[selectedGenre] || [] : [];
  }, [podcastsByGenre, selectedGenre]);

  const updateActiveSlides = (currentSlide) => {
    try {
      const slidesToShow =
        window.innerWidth > 1200 ? 5 : window.innerWidth > 900 ? 4 : window.innerWidth > 600 ? 3 : 1;

      const newActiveSlides = Array.from({ length: slidesToShow }, (_, i) => currentSlide + i).filter(
        (index) => index < filteredPodcasts.length
      );

      setActiveSlides(newActiveSlides);
    } catch (error) {
      console.error('Error updating active slides:', error);
    }
  };

  const handleBeforeChange = (_, next) => {
    if (isAutoScrolling) return;
    updateActiveSlides(next);
  };

  const handleAfterChange = (currentSlide) => {
    if (isAutoScrolling) return;
    updateActiveSlides(currentSlide);
  };

  const settings = useMemo(() => {
    return {
      ...getSliderSettings(filteredPodcasts),
      beforeChange: handleBeforeChange,
      afterChange: handleAfterChange,
    };
  }, [filteredPodcasts, isAutoScrolling]);

  const handleClose = () => {
    clearAllTimers();
    setIsAutoScrolling(false);
    dispatch(setSelectedGenre(null));
  };

  const handleResetPodcastsByGenre = () => {
    clearAllTimers();
    setIsAutoScrolling(false);
    dispatch(setSelectedGenre(null));
    dispatch(resetPodcastsByGenre());
  };

  const safeSlickGoTo = (slideToGo) => {
    try {
      if (sliderRef?.current?.slickGoTo) {
        sliderRef.current.slickGoTo(slideToGo);
        return true;
      }
    } catch (error) {
      console.error('Error in slickGoTo:', error);
    }
    return false;
  };

  useEffect(() => {
    if (!currentPodcast || !filteredPodcasts.length || !sliderRef?.current || isAutoScrolling) return;

    try {
      const currentPodcastIndex = filteredPodcasts.findIndex((podcast) => {
        return podcast.name === currentPodcast.collectionName && podcast.artist === currentPodcast.artistName;
      });

      if (currentPodcastIndex === -1) return;

      const isMobile = window.matchMedia('(max-width: 600px)').matches;
      const slidesToShow = isMobile ? 1 : window.innerWidth > 1200 ? 5 : window.innerWidth > 900 ? 4 : 3;

      const centerOffset = Math.floor(slidesToShow / 2);
      const slideToGo = Math.max(0, currentPodcastIndex - centerOffset);
      const sliderParentElement = sliderRef.current?.innerSlider?.list?.parentElement;

      if (!sliderParentElement) return;

      setIsAutoScrolling(true);

      sliderParentElement.classList.add('auto-scrolling');
      const success = safeSlickGoTo(slideToGo);

      if (success) {
        setAndTrackTimeout(() => {
          if (sliderParentElement) {
            sliderParentElement.classList.remove('auto-scrolling');
          }

          updateActiveSlides(slideToGo);

          setIsAutoScrolling(false);
        }, 1500);
      } else {
        setIsAutoScrolling(false);
      }
    } catch (error) {
      console.error('Error in auto-scroll effect:', error);
      setIsAutoScrolling(false);
    }
  }, [currentPodcast, filteredPodcasts]);

  if (filteredPodcasts.length === 0) {
    return (
      <Box className={classes.carouselContent}>
        {loading ? (
          <CircularProgress sx={{ margin: '20px auto' }} />
        ) : (
          <>
            <Box className={classes.closeButtonWrapper}>
              <IconButton className={classes.closeButton} onClick={handleResetPodcastsByGenre}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Typography className={classes.noPodcastsText}>No podcasts found for {selectedGenre}</Typography>
          </>
        )}
      </Box>
    );
  }

  return (
    <Box
      className={cx(classes.carouselContent, {
        visible: isVisible,
      })}
    >
      <Box className={classes.closeButtonWrapper}>
        <IconButton className={classes.closeButton} onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Slider ref={sliderRef} {...settings} className={classes.slider}>
        {filteredPodcasts.map((podcast, index) => (
          <PodcastCard
            classes={classes}
            key={podcast._id || index}
            podcast={podcast}
            hidden={activeSlides.includes(index) ? undefined : true}
          />
        ))}
      </Slider>
    </Box>
  );
};

export default PodcastsCarousel;
